import { useEffect, useRef } from "react";
import { AlertTriangle, Circle, Clock, Flag, Link2, Lock } from "lucide-react";

import { Badge } from "@/atoms";
import { TaskPriority } from "@/enums";
import type { KanbanBoardInterface, TaskInterface } from "@/interfaces";
import { t } from "@/hooks";
import { cn } from "@/utils";

const COLUMNS = [
    { status: "backlog", label: "Backlog" },
    { status: "ready", label: "Ready" },
    { status: "in_progress", label: "In Progress" },
    { status: "qa", label: "QA" },
    { status: "qa_blocked", label: "QA Blocked" },
    { status: "done", label: "Done" },
    { status: "production", label: "Production" },
] as const;

const COLUMN_COLORS: Record<string, string> = {
    backlog: "bg-text-muted",
    ready: "bg-primary",
    in_progress: "bg-warning",
    qa: "bg-[#ec4899]",
    qa_blocked: "bg-[#dc2626]",
    done: "bg-success",
    production: "bg-[#0ea5e9]",
};

const PRIORITY_VARIANT: Record<string, "error" | "warning" | "default" | "secondary"> = {
    critical: "error",
    high: "warning",
    medium: "default",
    low: "secondary",
};

export interface BoardViewProps {
    kanban: KanbanBoardInterface;
    setSelectedTask: (task: TaskInterface) => void;
    setDialogOpen: (open: boolean) => void;
    highlightTaskId?: string | null;
}

export const BoardView = ({
    kanban,
    setSelectedTask,
    setDialogOpen,
    highlightTaskId,
}: BoardViewProps) => {
    return (
        <div className="flex flex-1 gap-4 pb-4 overflow-x-auto min-h-[calc(100vh-14rem)]">
            {COLUMNS.map(({ status, label }) => {
                const columnTasks = kanban[status] ?? [];

                return (
                    <div
                        key={status}
                        className="flex flex-col w-80 shrink-0 bg-surface rounded-xl overflow-hidden border border-border"
                    >
                        <div className="p-3 bg-muted/50 border-b border-border flex items-center justify-between sticky top-0 z-10 hidden-scrollbar">
                            <h3 className="text-sm font-bold text-text-dark flex items-center gap-2">
                                <Circle className={cn("h-2.5 w-2.5 fill-current", COLUMN_COLORS[status].replace("bg-", "text-"))} />
                                {t(label)}
                            </h3>
                            <span className="text-[10px] font-semibold text-text-muted bg-surface px-2 py-0.5 rounded-full border border-border">
                                {columnTasks.length}
                            </span>
                        </div>

                        <div className="flex-1 p-2 overflow-y-auto space-y-2 relative pointer-events-auto">
                            {columnTasks.length === 0 && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <p className="text-xs text-text-muted italic">{t("No tasks")}</p>
                                </div>
                            )}

                            {columnTasks.map((task) => {
                                const blockedByDep = task.is_blocked_by_dependency === true;
                                const isCritical = task.priority === "critical";
                                const isOverdue = task.dead_time_status === "overdue";
                                const isHighlighted = highlightTaskId === task.id;
                                return (
                                <HighlightCard
                                    key={task.id}
                                    highlight={isHighlighted}
                                    className={cn(
                                        "bg-surface rounded-lg p-3 border shadow-sm hover:shadow-md transition-all z-20 pointer-events-auto relative cursor-pointer",
                                        task.is_blocked ? "border-error bg-error/10 ring-1 ring-error/30" : blockedByDep ? "border-error bg-error/5" : "border-border",
                                        !task.is_blocked && !blockedByDep && "hover:border-primary"
                                    )}
                                    onClick={() => { setSelectedTask(task); setDialogOpen(true); }}
                                >
                                    {task.is_blocked && (
                                        <div className="absolute inset-0 rounded-lg bg-error/5 pointer-events-none" />
                                    )}
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                            <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0", COLUMN_COLORS[task.status ?? "backlog"], "text-white")}>
                                                {task.task_number ?? task.id}
                                            </span>
                                            {task.is_blocked && <Lock className="h-3 w-3 text-error shrink-0" />}
                                            {isCritical && <Flag className="h-3 w-3 text-error shrink-0" aria-label={t("Critical priority")} />}
                                            {blockedByDep && <Link2 className="h-3 w-3 text-error shrink-0" aria-label={t("Blocked by dependency")} />}
                                            {task.dead_time_status === "approaching" && <Clock className="h-3 w-3 text-warning shrink-0" aria-label={t("Approaching deadline")} />}
                                            {isOverdue && <Clock className="h-3 w-3 text-error shrink-0" aria-label={t("Overdue")} />}
                                        </div>
                                        {task.is_blocked && (
                                            <span className="text-[9px] font-bold text-error bg-error/10 border border-error/30 px-1.5 py-0.5 rounded shrink-0">
                                                {t("Blocked")}
                                            </span>
                                        )}
                                    </div>

                                    <h4 className={cn("text-sm font-semibold mb-2 leading-tight line-clamp-2", isCritical ? "text-error" : "text-text-dark")}>
                                        {task.title}
                                    </h4>

                                    {(task as TaskInterface & { has_blocked_prerequisite?: boolean }).has_blocked_prerequisite && (
                                        <div className="flex items-center gap-1 mb-2">
                                            <AlertTriangle className="h-3 w-3 text-warning shrink-0" />
                                            <span className="text-[10px] font-medium text-warning">{t("Prerequisite blocked")}</span>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-1 mb-3 flex-wrap">
                                        {task.tags.map((tag) => (
                                            <span key={typeof tag === "string" ? tag : tag.id} className="text-[9px] font-medium text-text-secondary bg-muted px-1.5 py-0.5 rounded">
                                                #{typeof tag === "string" ? tag : tag.tag}
                                            </span>
                                        ))}
                                    </div>

                                    {task.dead_time && (
                                        <p className={cn("text-[10px] mb-2", isOverdue ? "text-error font-semibold" : "text-text-muted")}>
                                            {isOverdue ? "⚠ " : ""}{new Date(task.dead_time).toLocaleDateString()}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-1.5">
                                        <Badge variant={PRIORITY_VARIANT[task.priority as TaskPriority] ?? "default"} className="text-[9px] px-1.5 py-0.5 rounded shadow-sm relative z-30">
                                            {t(task.priority.charAt(0).toUpperCase() + task.priority.slice(1))}
                                        </Badge>
                                        {(task.story_points ?? 0) > 0 && (
                                            <span className="text-[10px] font-semibold text-text-muted bg-muted rounded-full h-5 w-5 flex items-center justify-center relative z-30">
                                                {task.story_points}
                                            </span>
                                        )}
                                        {task.assignee && (
                                            <span className="ms-auto text-[10px] font-medium bg-primary/10 text-primary rounded-full px-1.5 py-0.5">
                                                {task.assignee.avatar_initials}
                                            </span>
                                        )}
                                    </div>
                                </HighlightCard>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const HighlightCard = ({ highlight, className, children, onClick }: { highlight: boolean; className: string; children: React.ReactNode; onClick: () => void }) => {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!highlight || !ref.current) return;
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        ref.current.classList.add("ring-2", "ring-primary", "ring-offset-2");
        const t = setTimeout(() => ref.current?.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 1000);
        return () => clearTimeout(t);
    }, [highlight]);
    return <div ref={ref} className={className} onClick={onClick}>{children}</div>;
};
